import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { create, createOptions, ViewType, RichEditUnit, DocumentFormat } from "devexpress-richedit";

import "devextreme/dist/css/dx.light.css";

const DWRichEditComponent = forwardRef(
  ({ documentWizardOutputData, documentAsRtf, documentName, childDocumentWizardOutputData, childHtmlData }, ref) => {
    const richRef = useRef(null);
    const containerRef = useRef(null);

    const childRichRefs = useRef({});
    const childContainerRefs = useRef({});

    const lastMainDocumentRef = useRef(null);
    const lastChildDocumentsRef = useRef({});

    const [activeTab, setActiveTab] = useState("MAIN");

    const rtfToBase64 = (rtf) => {
      const bytes = new TextEncoder().encode(rtf);

      let binary = "";

      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }

      return btoa(binary);
    };

    const getDocumentBase64 = (document) => {
      if (!document) {
        return null;
      }

      if (document.startsWith("{\\rtf")) {
        return rtfToBase64(document);
      }

      return document;
    };

    const createEditorOptions = () => {
      const options = createOptions();

      options.unit = RichEditUnit.Inch;
      options.view.viewType = ViewType.PrintLayout;
      options.confirmOnLosingChanges.enabled = false;
      options.width = "100%";
      options.height = "980px";

      return options;
    };

    const childDocs =Array.isArray(childHtmlData) && childHtmlData.length ? childHtmlData : childDocumentWizardOutputData || [];
    useImperativeHandle(ref, () => ({
      getCurrentDocument: () => {
        return new Promise((resolve, reject) => {
          const editor = richRef.current;

          if (!editor) {
            reject(new Error("RichEdit editor is not initialized."));
            return;
          }

          try {
            editor.exportToBase64((base64) => {
              if (!base64) {
                reject(new Error("RichEdit returned an empty document."));
                return;
              }

              resolve(base64);
            }, DocumentFormat.Rtf);
          } catch (error) {
            reject(error);
          }
        });
      },

      getCurrentChildDocument: (tabId) => {
        return new Promise((resolve, reject) => {
          const editor = childRichRefs.current[tabId];

          if (!editor) {
            resolve(null);
            return;
          }

          try {
            editor.exportToBase64((base64) => {
              resolve(base64 || null);
            }, DocumentFormat.Rtf);
          } catch (error) {
            reject(error);
          }
        });
      },
    }));
    useEffect(() => {
      if (!containerRef.current) {
        return;
      }

      const editor = create(containerRef.current, createEditorOptions());

      richRef.current = editor;

      return () => {
        try {
          editor.dispose();
        } catch (error) {
          console.error("Error disposing main RichEdit:", error);
        }

        if (richRef.current === editor) {
          richRef.current = null;
        }
      };
    }, []);

    useEffect(() => {
      const editor = richRef.current;

      if (!editor || !documentAsRtf) {
        return;
      }

      if (lastMainDocumentRef.current === documentAsRtf) {
        return;
      }

      const base64 = getDocumentBase64(documentAsRtf);

      if (!base64) {
        return;
      }

      lastMainDocumentRef.current = documentAsRtf;

      try {
        editor.openDocument(base64, documentName || "Document", DocumentFormat.Rtf);
      } catch (error) {
        console.error("Unable to open main RichEdit document:", error);
      }
    }, [documentAsRtf, documentName]);

    useEffect(() => {
      if (!activeTab || activeTab === "MAIN") {
        return;
      }

      const [, indexString] = activeTab.split("_");

      const index = Number(indexString);

      if (Number.isNaN(index)) {
        return;
      }

      const child = childDocs?.[index];

      if (!child?.sReportRtf) {
        return;
      }

      const container = childContainerRefs.current[activeTab];

      if (!container) {
        return;
      }

      const base64 = getDocumentBase64(child.sReportRtf);

      if (!base64) {
        return;
      }

      let editor = childRichRefs.current[activeTab];

      if (!editor) {
        try {
          editor = create(container, createEditorOptions());

          childRichRefs.current[activeTab] = editor;
        } catch (error) {
          console.error("Unable to create child RichEdit:", error);

          return;
        }
      }
      if (lastChildDocumentsRef.current[activeTab] === child.sReportRtf) {
        return;
      }

      lastChildDocumentsRef.current[activeTab] = child.sReportRtf;

      try {
        editor.openDocument(base64, child.sName || "Document", DocumentFormat.Rtf);
      } catch (error) {
        console.error("Unable to open child RichEdit document:", error);
      }
    }, [activeTab, childDocs]);

    useEffect(() => {
      setActiveTab("MAIN");

      Object.values(childRichRefs.current).forEach((editor) => {
        try {
          editor?.dispose();
        } catch (error) {
          console.error("Error disposing child RichEdit:", error);
        }
      });

      childRichRefs.current = {};
      childContainerRefs.current = {};
      lastChildDocumentsRef.current = {};
      lastMainDocumentRef.current = null;
    }, [documentWizardOutputData?.gReportCatalogID]);

    return (
      <>
        <div className="document-wizard-output-tab">
          <p
            className={documentWizardOutputData?.sName ? "html-data-tab-name" : ""}
            onClick={() => setActiveTab("MAIN")}
          >
            {documentWizardOutputData?.sName || ""}
          </p>

          {childDocs?.map((item, index) => {
            const tabId = `${item.gParentId}_${index}`;

            return (
              <p key={tabId} className="html-data-tab-name" onClick={() => setActiveTab(tabId)}>
                {item.sName}
              </p>
            );
          })}
        </div>

        <div className="document-wizardOutput-active-tab">
          <div
            className="editor-container"
            style={{
              display: activeTab === "MAIN" ? "block" : "none",
            }}
          >
            <div ref={containerRef} />
          </div>
          {childDocs?.map((child, index) => {
            const tabId = `${child.gParentId}_${index}`;

            return (
              <div
                key={tabId}
                className="editor-container"
                style={{
                  display: activeTab === tabId ? "block" : "none",
                }}
              >
                <div
                  ref={(element) => {
                    if (element) {
                      childContainerRefs.current[tabId] = element;
                    }
                  }}
                />
              </div>
            );
          })}
        </div>
      </>
    );
  },
);

export default DWRichEditComponent;
